import React, { useEffect, useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import moment from 'moment';

import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import useDebounce from '@/Hooks/useDebounce';
import { UserGroupResponse, UserResponse } from '@/Interfaces/I_userGroup';

import {
  createUserGroup,
  deleteUserGroup,
  fetchUserGroups,
  updateUserGroup,
  userGroupSearchUsers,
} from '@/Redux/slices/UserGroupsSlice';

import { timeTranslate } from '@/Utils/timeTranslate';

const { Column } = Table;

const UserGroups = () => {
  // Form and Redux setup
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const {
    data: userGroups,
    pagination,
    loading,
    searched,
  } = useAppSelector((state) => state.userGroups);

  // State management
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [searchUserString, setSearchUserString] = useState('');

  // Helper functions
  const handleAddGroup = () => {
    form.resetFields();
    setEditingKey(null);
    setModalVisible(true);
  };

  const handleEdit = (record: UserGroupResponse) => {
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      users: record.users.map((u) => ({
        value: u.id,
        label: u.name,
      })),
    });
    setUsers(record.users);
    setEditingKey(record.id);
    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    dispatch(deleteUserGroup(id));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const groupData = {
        name: values.name,
        description: values.description,
        userIds: form.getFieldValue('users')?.map((u: { value: number }) => u.value) || [],
      };

      if (editingKey) {
        dispatch(updateUserGroup({ id: editingKey, data: groupData }));
      } else {
        dispatch(createUserGroup(groupData));
      }

      setModalVisible(false);
      setEditingKey(null);
      form.resetFields();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingKey(null);
    form.resetFields();
  };

  const handleSearchUsers = (value: string) => {
    setSearchUserString(value);
  };

  useDebounce(
    () => {
      if (searchUserString) {
        dispatch(userGroupSearchUsers(searchUserString));
      }
    },
    500,
    [searchUserString, dispatch],
  );

  const handleChangeUsers = (value: number[]) => {
    const allUsers = [...new Set([...users, ...(searched?.users || [])])];
    const newUsers = allUsers
      .filter((user) => value.includes(user.id))
      .map((user) => ({
        value: user.id,
        label: user.name,
      }));
    setUsers(allUsers);
    form.setFieldsValue({ users: newUsers });
  };

  // Effects
  useEffect(() => {
    dispatch(fetchUserGroups({ search, page: currentPage - 1, size: pageSize }));
  }, [dispatch, search, currentPage, pageSize]);

  // Render functions
  const renderEmptyState = () => (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No User Groups" />
  );

  const renderTable = () => (
    <Table
      dataSource={userGroups}
      loading={loading}
      rowKey="id"
      pagination={{
        total: pagination?.total || 0,
        current: typeof pagination?.page === 'number' ? pagination.page + 1 : 1,
        pageSize: pagination?.size || 10,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        showSizeChanger: true,
        onChange: (page, newPageSize) => {
          setCurrentPage(page);
          setPageSize(newPageSize);
        },
      }}
    >
      <Column title="ID" dataIndex="id" key="id" />
      <Column title="Name" dataIndex="name" key="name" />
      <Column title="Description" dataIndex="description" key="description" />
      <Column
        width="25vw"
        title="Users"
        dataIndex="users"
        key="users"
        render={(u: UserResponse[]) =>
          u.length === 0 ? (
            '-'
          ) : (
            <>
              {u.slice(0, 5).map((user) => (
                <Tag key={user.id}>{user.name}</Tag>
              ))}
              {u.length > 5 && `and ${u.length - 5} more`}
            </>
          )
        }
      />
      <Column
        title="Creation Date"
        dataIndex="creationDate"
        key="creationDate"
        render={(text: string) => timeTranslate(moment(text).unix(), true)}
      />
      <Column
        title="Last Modified"
        dataIndex="lastModifiedDate"
        key="lastModifiedDate"
        render={(text: string) => timeTranslate(moment(text).unix(), true)}
      />
      <Column
        title="Operation"
        key="operation"
        render={(_: unknown, record: UserGroupResponse) => (
          <Space>
            <Typography.Link onClick={() => handleEdit(record)}>Edit</Typography.Link>
            <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record.id)}>
              <Typography.Link type="danger">Delete</Typography.Link>
            </Popconfirm>
          </Space>
        )}
      />
    </Table>
  );

  return (
    <div className="h-auto bg-white p-6 pb-2">
      <div className="flex justify-between pb-2">
        <Input.Search
          placeholder="Search user group name"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <Button className="mb-1" onClick={handleAddGroup} type="primary" icon={<PlusOutlined />}>
          Add User Group
        </Button>
      </div>
      {!loading && userGroups?.length === 0 ? renderEmptyState() : renderTable()}

      <Modal
        title={editingKey ? 'Edit User Group' : 'Add User Group'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={handleCancel}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please input group name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please input group description!' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item name="users" label="Users">
            <Select
              filterOption={false}
              mode="multiple"
              placeholder="Select users"
              options={(searched?.users || []).map((user) => ({
                label: user.name,
                value: user.id,
              }))}
              notFoundContent={searched.loadingUsers ? <Spin size="small" /> : 'No results found'}
              onSearch={handleSearchUsers}
              onChange={handleChangeUsers}
              loading={searched.loadingUsers}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserGroups;
